'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ArrowRight, Package, Check } from 'lucide-react';
import { useState } from 'react';
import { useDesign } from '@/context/DesignContext';

interface HeroParapharmaProps {
  badge?: string;
  title?: string;
  subtitle?: string;
  description?: string;
  products?: Array<{
    name: string;
    subtitle: string;
    price: string;
    image: string;
  }>;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  stats?: Array<{
    value: string;
    label: string;
  }>;
  design?: any;
}

export function HeroParapharma({
  badge = 'Nouvelle Collection 2024',
  title = 'Votre bien-être,',
  subtitle = 'notre priorité',
  description = 'Découvrez notre sélection exclusive de produits parapharmaceutiques haut de gamme, soigneusement choisis pour votre santé et votre beauté.',
  products = [
    { name: 'Sérum Anti-âge Élixir', subtitle: 'Régénération cellulaire', price: '89,90€', image: 'bg-gradient-to-br from-rose-100 to-pink-200' },
    { name: 'Vitamine D3 Pure', subtitle: 'Bien-être quotidien', price: '24,90€', image: 'bg-gradient-to-br from-blue-100 to-cyan-200' },
    { name: 'Crème Hydratante Luxe', subtitle: 'Hydratation 24H', price: '45,90€', image: 'bg-gradient-to-br from-amber-100 to-orange-200' }
  ],
  primaryButtonText = 'Découvrir nos produits',
  secondaryButtonText = 'Nos services',
  stats = [
    { value: '15K+', label: 'Clients' },
    { value: '500+', label: 'Produits' },
    { value: '4.9', label: 'Note moyenne' }
  ],
  design: propDesign
}: HeroParapharmaProps) {
  const contextDesign = useDesign();
  const design = propDesign || contextDesign;
  const [activeProduct, setActiveProduct] = useState(0);

  // Extract design tokens with fallbacks
  const bgGradient = design?.componentDesignMap?.HeroParapharma?.backgroundGradient || 'linear-gradient(to bottom right, #F8FAFC, #FFFFFF, #EFF6FF)';
  const badgeBg = design?.componentDesignMap?.HeroParapharma?.badgeBackground || '#FFFFFF';
  const badgeTextColor = design?.componentDesignMap?.HeroParapharma?.badgeTextColor || design?.colors?.accent || '#F59E0B';
  const badgeBorderColor = design?.componentDesignMap?.HeroParapharma?.badgeBorderColor || design?.colors?.border || '#E2E8F0';
  const titleColor = design?.componentDesignMap?.HeroParapharma?.titleTextColor || design?.colors?.text || '#0F172A';
  const subtitleColor = design?.componentDesignMap?.HeroParapharma?.subtitleTextColor || design?.colors?.primary || '#3B82F6';
  const subtitleGradient = design?.componentDesignMap?.HeroParapharma?.subtitleGradient || 'linear-gradient(135deg, #3B82F6, #8B5CF6)';
  const descColor = design?.componentDesignMap?.HeroParapharma?.descriptionTextColor || design?.colors?.textLight || '#64748B';
  const primaryBtn = design?.componentDesignMap?.HeroParapharma?.primaryButtonStyle || design?.buttons?.primary;
  const secondaryBtn = design?.componentDesignMap?.HeroParapharma?.secondaryButtonStyle || design?.buttons?.secondary;
  const statValueColor = design?.componentDesignMap?.HeroParapharma?.statValueColor || design?.colors?.text || '#0F172A';
  const statLabelColor = design?.componentDesignMap?.HeroParapharma?.statLabelColor || design?.colors?.textLight || '#64748B';
  const productCardBg = design?.componentDesignMap?.HeroParapharma?.productCardBackground || '#FFFFFF';
  const productCardShadow = design?.componentDesignMap?.HeroParapharma?.productCardShadow || design?.effects?.boxShadow || '0 10px 25px -5px rgba(0,0,0,0.05)';

  return (
<section 
  className="relative pb-20 lg:pb-32 overflow-hidden" 
  style={{ 
    paddingTop: '140px', 
    background: bgGradient,
    display: 'block'
  }}
>  
  <div className="container mx-auto px-6 relative z-10" style={{ marginTop: '80px' }}>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full shadow-sm border mb-6"
              style={{ background: badgeBg, borderColor: badgeBorderColor }}
            >
              <Sparkles className="w-4 h-4" style={{ color: badgeTextColor }} />
              <span className="text-sm font-medium" style={{ color: design?.colors?.text || '#334155' }}>{badge}</span>
            </motion.div>
            
            <h1 className="text-5xl lg:text-7xl font-bold leading-tight mb-6" style={{ color: titleColor }}>
              {title}
              <span className="block bg-clip-text text-transparent" style={{ backgroundImage: subtitleGradient, WebkitBackgroundClip: 'text' }}>
                {subtitle}
              </span>
            </h1>
            
            <p className="text-lg mb-8 max-w-lg leading-relaxed" style={{ color: descColor }}>
              {description}
            </p>

            <div className="flex flex-wrap gap-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="rounded-full font-semibold flex items-center gap-3 transition-colors"
                style={{
                  backgroundColor: primaryBtn?.backgroundColor || '#0F172A',
                  color: primaryBtn?.textColor || '#FFFFFF',
                  padding: primaryBtn?.padding || '12px 28px',
                }}
                onMouseEnter={(e) => {
                  if (primaryBtn?.hoverBackgroundColor) e.currentTarget.style.backgroundColor = primaryBtn.hoverBackgroundColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = primaryBtn?.backgroundColor || '#0F172A';
                }}
              >
                <span>{primaryButtonText}</span>
                <ArrowRight className="w-5 h-5" />
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="rounded-full font-semibold transition-colors"
                style={{
                  backgroundColor: secondaryBtn?.backgroundColor || 'transparent',
                  border: `2px solid ${secondaryBtn?.borderColor || '#E2E8F0'}`,
                  color: secondaryBtn?.textColor || '#334155',
                  padding: secondaryBtn?.padding || '12px 28px',
                }}
                onMouseEnter={(e) => {
                  if (secondaryBtn?.hoverBorderColor) e.currentTarget.style.borderColor = secondaryBtn.hoverBorderColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = secondaryBtn?.borderColor || '#E2E8F0';
                }}
              >
                {secondaryButtonText}
              </motion.button>
            </div>

            <div className="flex gap-8 mt-12 pt-8 border-t" style={{ borderColor: design?.colors?.border || '#E2E8F0' }}>
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                >
                  <div className="text-3xl font-bold" style={{ color: statValueColor }}>{stat.value}</div>
                  <div className="text-sm" style={{ color: statLabelColor }}>{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeProduct}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.5 }}
                  className={`aspect-square rounded-3xl ${products[activeProduct].image} flex items-center justify-center`}
                  style={{ boxShadow: productCardShadow }}
                >
                  <div className="text-center">
                    <Package className="w-32 h-32 mx-auto mb-4" style={{ color: `${design?.colors?.text}4D` || '#0F172A4D' }} />
                    <p className="text-2xl font-bold" style={{ color: design?.colors?.text || '#1E293B' }}>{products[activeProduct].name}</p>
                    <p className="text-slate-600" style={{ color: design?.colors?.textLight || '#64748B' }}>{products[activeProduct].subtitle}</p>
                    <p className="text-3xl font-bold mt-4" style={{ color: design?.colors?.text || '#0F172A' }}>{products[activeProduct].price}</p>
                  </div>
                </motion.div>
              </AnimatePresence>

              <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 flex gap-3">
                {products.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveProduct(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      activeProduct === index ? 'w-8' : ''
                    }`}
                    style={{
                      backgroundColor: activeProduct === index ? (design?.colors?.text || '#0F172A') : (design?.colors?.border || '#CBD5E1'),
                    }}
                  />
                ))}
              </div>
            </div>

            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="absolute -top-4 -right-4 p-4 rounded-2xl shadow-xl border"
              style={{ background: design?.colors?.background || '#FFFFFF', borderColor: design?.colors?.border || '#E2E8F0' }}
            >
              <div className="flex items-center gap-2">
                <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: design?.colors?.success ? `${design.colors.success}33` : '#10B98133' }}
            >

                  <Check className="w-5 h-5" style={{ color: design?.colors?.success || '#10B981' }} />
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: design?.colors?.text || '#0F172A' }}>En stock</p>
                  <p className="text-xs" style={{ color: design?.colors?.textLight || '#64748B' }}>Livraison rapide</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}